// React imports
import { useState, useEffect } from 'react';
import { useNavigate, useOutletContext, useParams } from 'react-router-dom';

// Material UI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import Autocomplete from '@mui/material/Autocomplete';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import FormControlLabel from '@mui/material/FormControlLabel';
import Checkbox from '@mui/material/Checkbox';
import RadioGroup from '@mui/material/RadioGroup';
import Radio from '@mui/material/Radio';
import FormControl from '@mui/material/FormControl';
import { grey } from '@mui/material/colors';
import { createFilterOptions } from '@mui/material/Autocomplete';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSquareCheck, faSquare } from '@fortawesome/pro-light-svg-icons';
import { faCalculator, faCheck, faXmark, faFloppyDisk } from '@fortawesome/pro-solid-svg-icons';

// Generic imports
import _ from 'lodash';
import pluralize from 'pluralize';
import axios from 'axios';

// Local imports
import Header from '../../Header.js';

function AutoPricing() {
    const { org, accesstoken, accounts, autoPricings, onUpdateAutoPricing } = useOutletContext();
    const { autoPricingId } = useParams();
    const navigate = useNavigate();
    const [saving, setSaving] = useState(false);
    const [savingSuccess, setSavingSuccess] = useState(null);
    const [selectedAccounts, setSelectedAccounts] = useState([]);
    const [ratesRules, setRatesRules] = useState(['portal-contract', 'portal-spot', 'monada-contract', 'monada-spot', 'company-tariff']);
    const [maxRates, setMaxRates] = useState(10);
    const [sortBy, setSortBy] = useState('transit');
    const [margin, setMargin] = useState(0);
    const [errors, setErrors] = useState([]);
    const [name, setName] = useState('');

    useEffect(() => {
        if (autoPricingId === 'new') return;
        if (!autoPricings) return;
        const autoPricing = autoPricings.find(t => t.id === autoPricingId);
        if (!autoPricing) return;
        setSelectedAccounts(accounts.filter(a => autoPricing.accounts.includes(a.id)));
        setRatesRules(autoPricing.ratesRules || ['portal-contract', 'portal-spot', 'monada-contract', 'monada-spot', 'company-tariff']);
        setMaxRates(autoPricing.maxRates || 10);
        setSortBy(autoPricing.sortBy || 'transit');
        setMargin(autoPricing.margin || 0);
        setName(autoPricing.name || '');
    }, [ autoPricingId, autoPricings ]);

    async function onSaveDetails() {
        const _errors = [];

        if (_.isEmpty(name)) {
            _errors.push('name');
        }

        if (_.isEmpty(selectedAccounts)) {
            _errors.push('accounts');
        }

        if (_.isEmpty(ratesRules)) {
            _errors.push('ratesRules');
        }

        if ((parseInt(maxRates) || 0) <= 0) {
            _errors.push('maxRates');
        }

        setErrors(_errors);
        if (_errors.length > 0) return;
        setSaving(true);

        const autoPricing = {
            accounts: selectedAccounts.map(a => a.id),
            ratesRules,
            maxRates: parseInt(maxRates),
            sortBy,
            margin: parseFloat(margin),
            name,
        };

        try {
            if (autoPricingId === 'new') {
                const { data: updatedAutoPricing } = await axios.post(`/api/autopricings?accesstoken=${accesstoken}&organizationId=${org.id}`, { autoPricing: _.omit(autoPricing, 'log') });
                const newId = updatedAutoPricing.id;
                onUpdateAutoPricing({ autoPricing: updatedAutoPricing, delete: false });
                setSaving(false);
                setSavingSuccess('yes');
                navigate(window.location.pathname.replace('new', newId), { replace: true });
            } else {
                const { data: updatedAutoPricing } = await axios.put(`/api/autopricings/${autoPricingId}?accesstoken=${accesstoken}&organizationId=${org.id}`, { autoPricing: _.omit(autoPricing, 'log') });
                onUpdateAutoPricing({ autoPricing: updatedAutoPricing, delete: false });
                setSaving(false);
                setSavingSuccess('yes');
            }
        } catch (e) {
            setSaving(false);
            setSavingSuccess('no');
            setErrors([e.message]);
            console.log('Error saving:', e);
        }

        await new Promise(res => setTimeout(res, 3000));
        setSavingSuccess(null);
    }

    return (
        <Box>
            <Header
                title={'Automatic Pricing'}
                icon={faCalculator}
                onBack={() => navigate(-1)}
                buttons={[{
                    title: 'Save',
                    icon: savingSuccess === 'yes' ? faCheck : savingSuccess === 'no' ? faXmark : faFloppyDisk,
                    color: savingSuccess === 'yes' ? 'success' : savingSuccess === 'no' ? 'error' : 'monada-button',
                    action: onSaveDetails,
                    thinking: saving,
                }]}
            />
            <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Name
                        </Typography>
                    </Box>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <TextField
                            label='Name'
                            fullWidth
                            value={name}
                            onChange={e => setName(e.target.value)}
                            error={errors.includes('name')}
                            helperText={errors.includes('name') && 'Name is required. This name will be used to identify the auto-pricing in the system.'}
                        />
                    </Box>
                    {!errors.includes('name') && (
                        <Typography variant='caption' component='div' sx={{ paddingBottom: '12px' }}>
                            This name will be used to identify the auto-pricing in the system.
                        </Typography>
                    )}
                </Box>
            </Paper>
            <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Accounts
                        </Typography>
                    </Box>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1 }}>
                    <Box>
                        <AccountSelector
                            accounts={accounts}
                            selectedAccounts={selectedAccounts}
                            setSelectedAccounts={setSelectedAccounts}
                        />
                    </Box>
                </Box>
                {errors.includes('accounts') && (
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                    <Typography variant='caption' color='error' component='div' sx={{ paddingBottom: '12px' }}>
                        Please select the accounts that you want to apply the auto-pricing to.
                        </Typography>
                    </Box>
                )}
            </Paper>
            <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Rate Selection Rules
                        </Typography>
                    </Box>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Typography variant='body1' component='div' sx={{ paddingBottom: '12px' }}>
                    Please select which rates to use to build the quotes for the selected accounts.
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <FormControlLabel
                        control={<Checkbox checked={ratesRules.includes('portal-contract')} />}
                        label='Online Portals: Contract rates'
                        onChange={e => {
                            if (e.target.checked) {
                                setRatesRules([...ratesRules, 'portal-contract']);
                            } else {
                                setRatesRules(ratesRules.filter(r => r !== 'portal-contract'));
                            }
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ratesRules.includes('portal-spot')} />}
                        label='Online Portals: Spot rates'
                        onChange={e => {
                            if (e.target.checked) {
                                setRatesRules([...ratesRules, 'portal-spot']);
                            } else {
                                setRatesRules(ratesRules.filter(r => r !== 'portal-spot'));
                            }
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ratesRules.includes('monada-contract')} />}
                        label='Monada Supplier Rates: Contract rates'
                        onChange={e => {
                            if (e.target.checked) {
                                setRatesRules([...ratesRules, 'monada-contract']);
                            } else {
                                setRatesRules(ratesRules.filter(r => r !== 'monada-contract'));
                            }
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ratesRules.includes('monada-spot')} />}
                        label='Monada Supplier Rates: Spot rates'
                        onChange={e => {
                            if (e.target.checked) {
                                setRatesRules([...ratesRules, 'monada-spot']);
                            } else {
                                setRatesRules(ratesRules.filter(r => r !== 'monada-spot'));
                            }
                        }}
                    />
                    <FormControlLabel
                        control={<Checkbox checked={ratesRules.includes('company-tariff')} />}
                        label='Company Tariff'
                        onChange={e => {
                            if (e.target.checked) {
                                setRatesRules([...ratesRules, 'company-tariff']);
                            } else {
                                setRatesRules(ratesRules.filter(r => r !== 'company-tariff'));
                            }
                        }}
                    />
                </Box>
                {errors.includes('ratesRules') && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1, mt: 3 }}>
                        <Typography variant='caption' color='error' component='div' sx={{ paddingBottom: '12px' }}>
                            Please select at least one rate selection rule.
                        </Typography>
                    </Box>
                )}
            </Paper>
            <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Limit Results
                        </Typography>
                    </Box>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1 }}>
                    <TextField
                        label='Max number of quotes'
                        type='number'
                        value={maxRates}
                        onChange={e => setMaxRates(e.target.value)}
                    />
                    {errors.includes('maxRates') && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                            <Typography variant='caption' color='error' component='div' sx={{ paddingBottom: '12px' }}>
                                Please enter a valid number of quotes.
                            </Typography>
                        </Box>
                    )}
                    <Box>
                    <FormControl>
                        <RadioGroup
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                        >
                            <FormControlLabel
                                value='transit'
                                control={<Radio />}
                                label={`Top ${maxRates || 0} based on shortest transit time`}
                            />
                            <FormControlLabel
                                value='price' 
                                control={<Radio />}
                                label={`Top ${maxRates || 0} based on lowest price`}
                            />
                        </RadioGroup>
                    </FormControl>
                    </Box>
                </Box>
            </Paper>

            <Paper sx={{ padding: '24px', margin: '24px', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Typography variant='body2' component={Box} sx={{ fontWeight: 800, flex: 1 }}>
                            Margins
                        </Typography>
                    </Box>
                </Box>
                <hr style={{ opacity: 0.2 }} />
                <Box sx={{ display: 'flex', flexDirection: 'column', flexWrap: 'wrap', gap: 1 }}>
                    <Typography variant='body1' component='div' sx={{ paddingBottom: '12px' }}>
                        Please enter the margin you want to apply to the rates when creating quotes (excluding the company tariff).
                    </Typography>
                    <TextField
                        label='Margin'
                        type='number'
                        value={margin}
                        onChange={e => setMargin(e.target.value)}
                        InputProps={{
                            endAdornment: '%'
                        }}
                    />
                </Box>
            </Paper>
        </Box>
    );
}

export default AutoPricing;

function AccountSelector(props) {
    const { accounts, selectedAccounts, setSelectedAccounts } = props;
    const [ autocompleteInput, setAutocompleteInput ] = useState('');

    const sortedAccounts = _.uniq(_.sortBy(accounts, s => s.name).map(s => s.name));

    return (
        <>
            <Autocomplete
                disableCloseOnSelect={true}
                value={null}
                inputValue={autocompleteInput}
                onInputChange={( e, newValue, reason ) => {
                    if (reason !== 'reset') {
                        setAutocompleteInput(newValue);
                    }
                }}
                onChange={(e, newValue, reason) => {
                    if (reason === 'clear') {
                        setSelectedAccounts([]);
                    } else {
                        const isSelected = selectedAccounts.find(s => s.name.toUpperCase() === newValue.toUpperCase());
                        if (isSelected) {
                            setSelectedAccounts(selectedAccounts.filter(s => s.name.toUpperCase() !== newValue.toUpperCase()));
                        } else {
                            setSelectedAccounts([
                                ...selectedAccounts,
                                accounts.find(s => s.name.toUpperCase() === newValue.toUpperCase())
                            ]);
                        }
                    }
                }}
                onClick={e => { e.stopPropagation(); e.preventDefault(); }}
                options={sortedAccounts}
                fullWidth
                sx={{ flex: 1 }}
                filterOptions={(options, state) => filterOptions({ options, state, accounts })}
                renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                        <li key={key} {...rest} style={{ padding: '0px', margin: '0px 0px 0px 12px' }}>
                            <Checkbox
                                checkedIcon={<FontAwesomeIcon icon={faSquareCheck} />}
                                icon={<FontAwesomeIcon icon={faSquare} />}
                                checked={!!selectedAccounts.find(s => s.name.toUpperCase() === option.toUpperCase())}
                            />
                            <Box sx={{ display: 'flex', gap: '3px', flexDirection: 'column', marginLeft: '12px' }}>
                                <Box>
                                    {option}
                                </Box>
                                {autocompleteInput.startsWith('#') && (() => {
                                    const account = accounts.find(s => s.name.toUpperCase() === option.toUpperCase());
                                    return (
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px' }}>
                                            {account.tags?.map(t => (
                                                <Box sx={{ backgroundColor: grey[400], color: 'white', padding: '2px 6px', borderRadius: '24px', fontSize: '10px' }}>
                                                    {t.label}
                                                </Box>
                                            ))}
                                        </Box>
                                    )
                                })()}
                            </Box>
                        </li>
                    );
                }}
                renderInput={params => <TextField {...params} variant='outlined' placeholder='Search accounts...' />}
            />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '3px', marginTop: '12px', marginLeft: 1 }}>
                <Typography variant='caption' component='div'>
                    {selectedAccounts.length} {pluralize('account', selectedAccounts.length)} selected.
                </Typography>
                {selectedAccounts.length > 0 && (
                    <Button size='small' variant='text' color='primary' sx={{ fontSize: '12px !important', padding: '0px 12px', margin: '0px', textTransform: 'none' }} onClick={() => setSelectedAccounts([])}>
                        Clear
                    </Button>
                )}
            </Box>
        </>
    );
}

function filterOptions({ options, state, accounts }) {
    if (!state.inputValue.startsWith('#')) return createFilterOptions({})(options, state);
    if (state.inputValue.length === 1) return options;
    const tagLabel = state.inputValue.substring(1);
    const filtered = options.filter(o => {
        const account = accounts.find(s => s.name.toUpperCase() === o.toUpperCase());
        return account?.tags?.some(t => t.label.toUpperCase().includes(tagLabel.toUpperCase()));
    });
    return filtered;
}